/**
 * tool-handlers.ts — Phase 4 WebMCP tool handlers for Studio.
 *
 * Each handler is an `async (args) => unknown` that reads the studio store
 * and either dispatches a store action or routes through the existing
 * navigation machinery. Handlers throw `ToolGateError` when invoked in a
 * shell where they don't belong (e.g. cogo.block.edit in Dashboard); the
 * CogoAgent UI surfaces the message so the user knows why the call was
 * refused.
 *
 * Intentionally lightweight:
 *   - No new backend calls. Commerce updatePrice is a stub that logs +
 *     returns an ack so the confirmation-UI plumbing can be validated
 *     end-to-end.
 *   - IDE Page / ActionFlow / Ontology-facing tools delegate to the existing
 *     cogo.studio.* MCP mutation bus so chat and direct UI controls share the
 *     same store, undo, validation, and persistence path.
 */

import { useStudioStore } from "@/store/studio-store";
import type { StudioRoute } from "@/store/types";
import { getCurrentShell, getCurrentThreadId } from "@/store/selectors";
import { buildPageContext } from "@/features/chat/services/chat-context";
import { useEditorStore } from "@/features/page-editor/store/editor-store";
import { useFlowEditorStore } from "@/features/flow-editor/store/flow-editor-store";
import { mcpBus } from "@/mcp/studio-mcp-bus";
import { pushStudioPath, studioRoutePath } from "@/lib/studio-navigation";
import { resolveStudioRouteFromPathname } from "@/lib/studio-route-map";
import { STUDIO_ROUTE_CANONICAL_PATH_BY_ROUTE } from "@/lib/studio-route-registry";

// ── Error type ─────────────────────────────────────────────

/**
 * Raised when a tool is called in a shell where it's not available
 * (e.g. `cogo.block.edit` outside IDE). The CogoAgent UI reads the
 * message and surfaces it as an error transcript entry.
 */
export class ToolGateError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ToolGateError";
  }
}

// ── Helpers ────────────────────────────────────────────────

function requireString(args: Record<string, unknown>, key: string): string {
  const v = args[key];
  if (typeof v !== "string" || v.length === 0) {
    throw new ToolGateError(`Missing required argument "${key}" (string).`);
  }
  return v;
}

function requireFiniteNumber(
  args: Record<string, unknown>,
  key: string,
): number {
  const v = args[key];
  if (typeof v !== "number" || !Number.isFinite(v)) {
    throw new ToolGateError(`Missing required argument "${key}" (number).`);
  }
  return v;
}

function requireObject(
  args: Record<string, unknown>,
  key: string,
): Record<string, unknown> {
  const v = args[key];
  if (!v || typeof v !== "object" || Array.isArray(v)) {
    throw new ToolGateError(`Missing required argument "${key}" (object).`);
  }
  return v as Record<string, unknown>;
}

function optionalString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0
    ? value.trim()
    : null;
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}

function requireIdeProject(): ReturnType<typeof useStudioStore.getState> {
  const state = useStudioStore.getState();
  if (getCurrentShell(state) !== "ide") {
    throw new ToolGateError(
      "This tool is only available in the IDE shell. Open a project first.",
    );
  }
  if (!state.currentProjectId || !state.currentProject) {
    throw new ToolGateError(
      "No project is currently loaded. Open a project before editing.",
    );
  }
  return state;
}

function resolvePageTarget(args: Record<string, unknown>): {
  blockName: string;
  elementId: string;
} {
  const editor = useEditorStore.getState();
  const studio = useStudioStore.getState();
  const pageBlockName = editor.app?.pages?.find(
    (page) => page.id === editor.activePageId,
  )?.blocks?.[0];
  const blockName =
    (typeof args.blockName === "string" && args.blockName) ||
    editor.selectedBlockName ||
    pageBlockName ||
    Object.keys(editor.blocks ?? {})[0] ||
    Object.keys(studio.currentProject?.blocks ?? {})[0];
  const elementId =
    (typeof args.elementId === "string" && args.elementId) ||
    editor.selectedIds?.[0];

  if (!blockName) {
    throw new ToolGateError(
      "No Page block is available. Open Page and select an element first.",
    );
  }
  if (!elementId) {
    throw new ToolGateError(
      "No Page element is selected. Select an element or pass elementId.",
    );
  }
  return { blockName, elementId };
}

function resolveFlowName(args: Record<string, unknown>): string {
  const flow = args.flow ?? args.flowName;
  if (typeof flow !== "string" || flow.length === 0) {
    throw new ToolGateError('Missing required argument "flowName" (string).');
  }
  return flow;
}

function assertFlowExists(blockName: string, flowName: string): void {
  const studio = useStudioStore.getState();
  const block = studio.currentProject?.blocks?.[blockName] as
    | { flow?: { flows?: Record<string, unknown> } | Record<string, unknown> }
    | undefined;
  const blockFlows = block?.flow;
  const declared =
    Boolean(
      (blockFlows as { flows?: Record<string, unknown> } | undefined)?.flows?.[
        flowName
      ],
    ) ||
    Boolean((blockFlows as Record<string, unknown> | undefined)?.[flowName]) ||
    Boolean(
      (studio.currentProject?.flows as Record<string, unknown> | undefined)?.[
        flowName
      ],
    );
  const hydrated = useFlowEditorStore
    .getState()
    .flows.some((flow) => flow.name === flowName);
  if (!declared && !hydrated) {
    throw new ToolGateError(
      `Flow "${flowName}" was not found in this project.`,
    );
  }
}

async function executeStudioCapabilityOperation(
  operationId: string,
  params: Record<string, unknown>,
): Promise<unknown> {
  const result = await mcpBus.executeWebMcp({
    tool: "cogo.studio.capability.executeOperation",
    params: { operationId, params },
  });
  if (!result.success) {
    throw new ToolGateError(
      result.error ?? `Studio operation "${operationId}" failed.`,
    );
  }
  return result.data;
}

export const STUDIO_CAPABILITY_GUARDED_ALIASES = [
  "cogo.page.updateElement",
  "cogo.actionflow.bindEvent",
  "cogo.ontology.bindSelectedPageFlow",
] as const;

export type StudioCapabilityGuardedAlias =
  (typeof STUDIO_CAPABILITY_GUARDED_ALIASES)[number];

export function isStudioCapabilityGuardedAlias(
  name: string,
): name is StudioCapabilityGuardedAlias {
  return (STUDIO_CAPABILITY_GUARDED_ALIASES as readonly string[]).includes(
    name,
  );
}

function resolveElementUpdateOperationParams(
  args: Record<string, unknown>,
): Record<string, unknown> {
  requireIdeProject();
  const { blockName, elementId } = resolvePageTarget(args);
  const changes = requireObject(args, "changes");
  return { blockName, elementId, changes };
}

function resolveEventBindingOperationParams(
  args: Record<string, unknown>,
): Record<string, unknown> {
  requireIdeProject();
  const { blockName, elementId } = resolvePageTarget(args);
  const eventType =
    typeof args.eventType === "string" && args.eventType
      ? args.eventType
      : "tap";
  const flowName = resolveFlowName(args);
  assertFlowExists(blockName, flowName);
  return {
    blockName,
    eventKey: `${elementId}:${eventType}`,
    flow: flowName,
    element: {
      elementId,
      eventType,
      binding: { flow: flowName },
    },
  };
}

/**
 * Convert legacy manifest aliases into canonical capability operations.
 * This is the only externally callable path for these aliases: it creates a
 * pending confirmation and never invokes a creator mutation with internal
 * authority.
 */
export async function planStudioCapabilityGuardedAlias(
  name: StudioCapabilityGuardedAlias,
  args: Record<string, unknown>,
): Promise<unknown> {
  if (name === "cogo.page.updateElement") {
    return executeStudioCapabilityOperation(
      "element.update",
      resolveElementUpdateOperationParams(args),
    );
  }

  const result = await executeStudioCapabilityOperation(
    "behavior.flow.connect-selected",
    resolveEventBindingOperationParams(args),
  );
  if (name === "cogo.ontology.bindSelectedPageFlow") {
    return {
      ...(result as Record<string, unknown>),
      surface: "ontology",
      repair: "bind-selected-page-flow",
    };
  }
  return result;
}

const ALLOWED_ROUTES = new Set<StudioRoute>(
  Object.keys(STUDIO_ROUTE_CANONICAL_PATH_BY_ROUTE) as StudioRoute[],
);

/**
 * Slice 6 §3.2c (2026-08-18): the reachability census
 * (`scripts/reachability-census.mjs`) classified exactly two registry routes
 * `defect` — registered, mounted, resolvable, and reached by NO live human
 * control. `map`: nothing in 1,405 production files navigates to it.
 * `design-system-edit`: its only anchor (`preset-binding-strip.tsx`) lives in
 * a component nothing imports. Deriving `ALLOWED_ROUTES` from the full
 * registry therefore made the agent surface LARGER than the human surface by
 * exactly these two routes. Until a door ships, the agent is held to the
 * human boundary. A route leaves this set by GAINING a door — re-run the
 * census to prove it — not by deleting its line here. Kept separate from
 * `ALLOWED_ROUTES` so the refusal can say the true reason instead of
 * pretending the route is unknown.
 */
const ROUTES_WITHOUT_A_HUMAN_DOOR = new Set<StudioRoute>([
  "map",
  "design-system-edit",
]);

// ── Handlers ───────────────────────────────────────────────

/**
 * cogo.nav.goto — navigate to a Studio route.
 *   - `projectId` present → opens the project (IDE shell).
 *   - Otherwise → sets the Studio route and updates URL.
 */
export async function handleNavGoto(
  args: Record<string, unknown>,
): Promise<unknown> {
  const route = requireString(args, "route") as StudioRoute;
  if (!ALLOWED_ROUTES.has(route)) {
    throw new ToolGateError(`Unknown Studio route: "${route}".`);
  }
  if (ROUTES_WITHOUT_A_HUMAN_DOOR.has(route)) {
    throw new ToolGateError(
      `Route "${route}" is registered but no reachable human control navigates to it; ` +
        "agent navigation is withheld until a door ships (slice 6 §3.2c).",
    );
  }
  const projectId =
    typeof args.projectId === "string" ? args.projectId : undefined;

  const store = useStudioStore.getState();

  if (projectId) {
    const url = `/studio?projectId=${encodeURIComponent(projectId)}`;
    window.history.pushState(null, "", url);
    await store.openServerProject(projectId);
    return { ok: true, route: "workspace", projectId };
  }

  // Close any open project so the dashboard/commerce shell re-renders.
  if (store.view === "workspace") {
    store.closeProject();
  }
  const url = studioRoutePath(route);
  // `ALLOWED_ROUTES` already gated `route`, so this resolves — but the
  // resolver is now honest about unknown paths (`null` instead of a silent
  // `'home'`), and a tool that navigated an agent somewhere it did not ask
  // for is exactly the failure that guard exists to prevent. Report it
  // instead of substituting a destination.
  const canonicalRoute = resolveStudioRouteFromPathname(url);
  if (!canonicalRoute) {
    throw new ToolGateError(
      `Studio route "${route}" has no registered path (resolved "${url}").`,
    );
  }
  pushStudioPath(url);
  store.setStudioRoute(canonicalRoute);
  return { ok: true, route: canonicalRoute, requestedRoute: route, url };
}

/** cogo.nav.currentContext — return shell / route / project / block. */
export async function handleNavCurrentContext(): Promise<unknown> {
  const state = useStudioStore.getState();
  const ctx = buildPageContext(state);
  return {
    ok: true,
    shell: ctx.shell,
    route: ctx.route,
    project: ctx.project ?? null,
    page: ctx.page ?? null,
    block: ctx.block ?? null,
    threadId: getCurrentThreadId(state),
  };
}

/**
 * cogo.project.create — open the New Project wizard prefilled with the
 * supplied name and optional template id.
 *
 * Studio doesn't expose a dedicated "prefill" store action, so we pass the
 * intent via URL query params (`/studio/new?name=…&template=…`) and let
 * the NewProjectWizardPage read them on mount. If the page ignores the
 * params today, the user still lands on the wizard and can type manually —
 * the tool call still succeeds (Phase 4 is about plumbing, not perfecting
 * every handler).
 */
export async function handleProjectCreate(
  args: Record<string, unknown>,
): Promise<unknown> {
  const name = requireString(args, "name");
  const templateId =
    typeof args.templateId === "string" ? args.templateId : undefined;

  const params = new URLSearchParams();
  params.set("name", name);
  if (templateId) params.set("template", templateId);
  const url = `/studio/new?${params.toString()}`;
  pushStudioPath(url);
  return {
    ok: true,
    stub: true,
    route: "/studio/new",
    name,
    templateId: templateId ?? null,
  };
}

/** cogo.project.open — open a project by id (IDE shell). */
export async function handleProjectOpen(
  args: Record<string, unknown>,
): Promise<unknown> {
  const projectId = requireString(args, "projectId");
  const url = `/studio?projectId=${encodeURIComponent(projectId)}`;
  window.history.pushState(null, "", url);
  await useStudioStore.getState().openServerProject(projectId);
  return { ok: true, projectId };
}

/**
 * cogo.block.edit — gated to IDE shell. Phase 4 stub: records the patch
 * and returns an ack so the confirmation flow can be exercised without
 * landing a full JSON-patch pipeline.
 */
export async function handleBlockEdit(
  args: Record<string, unknown>,
): Promise<unknown> {
  const state = useStudioStore.getState();
  if (getCurrentShell(state) !== "ide") {
    throw new ToolGateError(
      "cogo.block.edit is only available in the IDE shell. Open a project first.",
    );
  }
  if (!state.currentProjectId) {
    throw new ToolGateError(
      "No project is currently loaded. Open a project before editing blocks.",
    );
  }
  const blockName = requireString(args, "blockName");
  const patch = args.patch;
  if (patch === undefined || patch === null || typeof patch !== "object") {
    throw new ToolGateError('Missing required argument "patch" (object).');
  }
  // Phase 4 stub — log + return ack; follow-up PR wires real JSON-patch.
  // eslint-disable-next-line no-console
  console.info("[cogo.block.edit] stub patch recorded", { blockName, patch });
  return {
    ok: true,
    stub: true,
    projectId: state.currentProjectId,
    blockName,
    patchKeys: Object.keys(patch as Record<string, unknown>),
    message:
      "Block edit recorded (Phase 4 stub — JSON-patch application is a follow-up PR).",
  };
}

/** cogo.page.updateElement — mutate a Page element through the real Studio MCP bus. */
export async function handlePageUpdateElement(
  args: Record<string, unknown>,
): Promise<unknown> {
  return planStudioCapabilityGuardedAlias("cogo.page.updateElement", args);
}

/** cogo.actionflow.bindEvent — bind one Page event to an ActionFlow. */
export async function handleActionFlowBindEvent(
  args: Record<string, unknown>,
): Promise<unknown> {
  return planStudioCapabilityGuardedAlias("cogo.actionflow.bindEvent", args);
}

/**
 * cogo.ontology.bindSelectedPageFlow — execute the Ontology work-queue repair
 * for the selected Page element. It intentionally calls the same event-binding
 * handler as ActionFlow; Ontology supplies the judgment, not a second write path.
 */
export async function handleOntologyBindSelectedPageFlow(
  args: Record<string, unknown>,
): Promise<unknown> {
  return planStudioCapabilityGuardedAlias(
    "cogo.ontology.bindSelectedPageFlow",
    args,
  );
}

/**
 * cogo.studio.qwenDesignSystemDecision — Studio WebMCP control-plane contract.
 *
 * The browser registry exposes the tool shape so headless ontology gates and
 * Studio share one contract. It deliberately does not call Qwen from the
 * browser: live model calls require server/proxy credentials and must be
 * performed by the configured WebMCP endpoint or headless proxy path.
 */
export async function handleQwenDesignSystemDecision(
  args: Record<string, unknown>,
): Promise<unknown> {
  const responseContract = requireObject(args, "responseContract");
  const packet = requireObject(args, "packet");
  const sourceContext =
    packet.sourceContext &&
    typeof packet.sourceContext === "object" &&
    !Array.isArray(packet.sourceContext)
      ? (packet.sourceContext as Record<string, unknown>)
      : {};

  return {
    success: false,
    status: "blocked",
    schemaVersion: "cogo.studio.qwen-design-system-decision-tool.v1",
    tool: "cogo.studio.qwenDesignSystemDecision",
    modelRole: "qwen",
    actualTransport: "studio-webmcp-contract",
    finalWriter: "deterministic-cogo-compiler",
    responseContractSchemaVersion: optionalString(
      responseContract.schemaVersion,
    ),
    packetSchemaVersion: optionalString(packet.schemaVersion),
    evidenceRefCount: stringArray(sourceContext.evidenceRefs).length,
    error:
      "Studio WebMCP tool contract is registered, but live Qwen judgement must be served by a configured WebMCP endpoint or headless proxy path.",
  };
}

/**
 * cogo.commerce.product.updatePrice — gated to Commerce shell. Phase 4
 * stub — logs + returns ack. The real Commerce backend is out of scope.
 */
export async function handleCommerceUpdatePrice(
  args: Record<string, unknown>,
): Promise<unknown> {
  const state = useStudioStore.getState();
  if (getCurrentShell(state) !== "commerce") {
    throw new ToolGateError(
      "cogo.commerce.product.updatePrice is only available inside the Commerce shell.",
    );
  }
  const productId = requireString(args, "productId");
  const price = requireFiniteNumber(args, "price");
  if (price < 0) {
    throw new ToolGateError("Price must be >= 0.");
  }
  // eslint-disable-next-line no-console
  console.info("[cogo.commerce.product.updatePrice] stub", {
    productId,
    price,
  });
  return {
    ok: true,
    stub: true,
    productId,
    price,
    message:
      "Commerce update recorded (Phase 4 stub — backend wiring is a follow-up PR).",
  };
}

// ── Handler table ──────────────────────────────────────────

/** Map from tool manifest name → handler. Consumed by webmcp-setup. */
export const STUDIO_TOOL_HANDLERS: Record<
  string,
  (args: Record<string, unknown>) => Promise<unknown>
> = {
  "cogo.nav.goto": handleNavGoto,
  "cogo.nav.currentContext": handleNavCurrentContext,
  "cogo.project.create": handleProjectCreate,
  "cogo.project.open": handleProjectOpen,
  "cogo.block.edit": handleBlockEdit,
  "cogo.page.updateElement": handlePageUpdateElement,
  "cogo.actionflow.bindEvent": handleActionFlowBindEvent,
  "cogo.ontology.bindSelectedPageFlow": handleOntologyBindSelectedPageFlow,
  "cogo.studio.qwenDesignSystemDecision": handleQwenDesignSystemDecision,
  "cogo.commerce.product.updatePrice": handleCommerceUpdatePrice,
};
